/**
 * ReceiptLoopService — ขาบันทึกของ growth loop
 *
 * หน้าที่เดียว: ทำให้ใบเสร็จทุกใบที่ออกจาก POS ไปโผล่ในตาราง pos_receipts
 * เพื่อให้ v_loop_health คำนวณ install_per_receipt ได้จริง
 *
 * 🔴 กฎที่ห้ามแหก: การบันทึกต้องไม่มีทางขวางการขาย
 *    ร้านค้าไม่สนใจ metric ของเรา เขาสนใจว่าใบเสร็จออกไหม
 *    ทุกฟังก์ชันในไฟล์นี้จึงเป็น fire-and-forget และห้าม throw ออกไปข้างนอก
 *    ถ้าเน็ตหลุด -> เข้าคิวในเครื่อง แล้วส่งตอนออกใบถัดไป
 */

import supabaseService from './SupabaseService';
import { getMerchantCode, newShareToken } from '../utils/shareToken';

/** client ตัวเดียวกับที่ทั้งแอปใช้ — ไม่สร้างใหม่ ไม่งั้น session ไม่ตรงกัน */
const db = () => supabaseService?.client || null;

const QUEUE_KEY = 'moneyma_receipt_queue';
const MERCHANT_ID_KEY = 'moneyma_merchant_id';
const MERCHANT_NAME_KEY = 'moneyma_merchant_name';
const QUEUE_LIMIT = 500; // กันคิวบวมกรณีร้านออฟไลน์ยาว

// ── โดเมนที่ QR จะพาไป ───────────────────────────────────────────
// ตั้งผ่าน .env: REACT_APP_RECEIPT_BASE_URL=https://<โดเมนเว็บแอป>
//
// 🔴 ไม่มี fallback ไปที่ window.location.origin โดยตั้งใจ
//    เคยเขียนแบบนั้นแล้วได้พฤติกรรมที่ต่างกันสองแบบในบิลด์เดียว:
//    บนเว็บ loop ติดเอง (origin เป็น https) แต่บนแอป origin เป็น capacitor://
//    จึงเงียบ — แปลว่าสวิตช์ปิดไม่จริง และเว็บจะเริ่มเขียนข้อมูลก่อนรัน SQL
//    ตอนนี้กติกาเดียว: ไม่ตั้ง env = loop ปิดทุกแพลตฟอร์ม
function receiptBaseUrl() {
  const configured = process.env.REACT_APP_RECEIPT_BASE_URL;
  return configured ? configured.replace(/\/$/, '') : '';
}

/**
 * 🔴 สวิตช์ปิด-เปิดทั้ง loop อยู่ที่ตัวแปรเดียว: REACT_APP_RECEIPT_BASE_URL
 *
 *    ไม่ได้ตั้ง = ทุกอย่างในไฟล์นี้เป็นหมัน ไม่สร้าง token ไม่เขียนคิว
 *    ไม่ยิงเน็ต ใบเสร็จออกเหมือนเวอร์ชันที่อยู่บนสโตร์ทุกประการ
 *
 *    จงใจให้ค่าเริ่มต้นคือ "ปิด" เพราะถ้าเผลอ build ขึ้นสโตร์ก่อนรัน SQL
 *    ใบเสร็จทุกใบจะเข้าคิวในเครื่องผู้ใช้แล้วส่งไม่ขึ้นตลอดไป
 */
export const LOOP_ENABLED = Boolean(receiptBaseUrl());

/** ลิงก์เต็มที่จะฝังลง QR บนใบเสร็จ */
export function buildReceiptUrl(token) {
  const base = receiptBaseUrl();
  return base && token ? `${base}/r/?t=${token}` : '';
}

/** สร้าง token ใหม่ 1 ใบ — เรียกตอนสร้างเอกสาร ไม่ใช่ตอนพิมพ์
 *  คืน null เมื่อ loop ปิดอยู่ ซึ่งทำให้ทุกขั้นตอนถัดไปข้ามไปเอง */
export function issueShareToken() {
  return LOOP_ENABLED ? newShareToken(getMerchantCode()) : null;
}

// ── คิวออฟไลน์ ───────────────────────────────────────────────────
function readQueue() {
  try {
    const raw = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
}

function writeQueue(rows) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(rows.slice(-QUEUE_LIMIT)));
  } catch (e) {
    /* เต็มก็ปล่อย ไม่ให้กระทบการขาย */
  }
}

// ── ตัวร้าน ──────────────────────────────────────────────────────
function localMerchantId() {
  try {
    return localStorage.getItem(MERCHANT_ID_KEY) || null;
  } catch (e) {
    return null;
  }
}

const DEFAULT_MERCHANT_NAME = 'ร้านค้า (ยังไม่ตั้งชื่อ)';

/** ชื่อที่ส่งขึ้น Supabase สำเร็จล่าสุด — ใช้เทียบว่าต้องอัปเดตไหม */
function syncedMerchantName() {
  try {
    return localStorage.getItem(MERCHANT_NAME_KEY) || '';
  } catch (e) {
    return '';
  }
}

function rememberMerchantName(name) {
  try { localStorage.setItem(MERCHANT_NAME_KEY, name); } catch (e) { /* ไม่เป็นไร */ }
}

/** ตัดช่องว่างซ้อนและกันชื่อยาวเกินจอของหน้า /r/ */
function cleanShopName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').slice(0, 80);
}

/**
 * อัปเดตชื่อร้านอย่างเดียว
 *
 * จำชื่อลง localStorage เฉพาะตอนเขียนสำเร็จ — ถ้าพลาดเพราะออฟไลน์
 * รอบหน้าจะเห็นว่าชื่อยังไม่ตรงแล้วลองใหม่เอง ไม่ใช่เงียบไปตลอด
 */
async function pushMerchantName(merchantId, name) {
  const client = db();
  if (!client) return;
  try {
    const { error } = await client.from('merchants').update({ name }).eq('id', merchantId);
    if (!error) rememberMerchantName(name);
  } catch (e) {
    /* ออฟไลน์ ค่อยลองรอบหน้า */
  }
}

/** หาแถวร้านของผู้ใช้คนนี้ — คืน null ทั้งตอนไม่มีและตอนอ่านไม่ได้ */
async function findMerchantRow(client, userId) {
  try {
    const { data, error } = await client
      .from('merchants')
      .select('id, name')
      .eq('owner_user_id', userId)
      .maybeSingle();
    return error ? null : data || null;
  } catch (e) {
    return null;
  }
}

/**
 * รับแถวที่มีอยู่แล้วมาเป็นของเครื่องนี้
 * ชื่อบนเซิร์ฟเวอร์คือความจริง เครื่องนี้จะดันชื่อขึ้นก็ต่อเมื่อมีชื่อและไม่ตรงกัน
 */
function adoptMerchantRow(row, name) {
  try { localStorage.setItem(MERCHANT_ID_KEY, row.id); } catch (e) { /* ไม่เป็นไร */ }
  if (row.name) rememberMerchantName(row.name);
  if (name && name !== row.name) {
    // ไม่ await — เส้นทางนี้อยู่ในจังหวะขาย ห้ามให้ช้าตามเน็ต
    pushMerchantName(row.id, name);
  }
  return row.id;
}

/**
 * ผูกเครื่องนี้เข้ากับแถวใน merchants ครั้งแรกที่ออนไลน์
 *
 * 🔴 กุญแจคือ owner_user_id ไม่ใช่ merchant_code
 *    merchant_code เป็นรหัสสุ่มในเครื่อง ถ้าใช้มันเป็น conflict target
 *    สองร้านที่บังเอิญสุ่มได้รหัสเดียวกันจะถูกรวมเป็นร้านเดียว
 *
 * 🔴 ต้องล็อกอินก่อน — RLS ของ pos_receipts ผูกกับ auth.uid()
 *    ถ้ายังไม่ล็อกอิน ใบเสร็จจะค้างในคิวจนกว่าจะล็อกอิน (ไม่หาย)
 */
export async function ensureMerchant(profile = {}) {
  const name = cleanShopName(profile.name);
  const existing = localMerchantId();

  // 🔴 มีร้านแล้วก็ยังต้องเข้ามาตรงนี้ ห้าม return ทันทีแบบเวอร์ชันแรก
  //    เวอร์ชันแรกออกตั้งแต่บรรทัดนี้ ชื่อร้านที่ตั้งทีหลังจึงไม่มีวันขึ้นถึง Supabase
  //    ลูกค้าที่สแกน QR เห็น "ร้านค้า (ยังไม่ตั้งชื่อ)" ตลอดไปทั้งที่สลิปพิมพ์ชื่อจริง
  if (existing) {
    // เทียบกับชื่อที่ส่งสำเร็จล่าสุด -> ยิงเน็ตเฉพาะตอนชื่อเปลี่ยนจริง ไม่ใช่ทุกใบเสร็จ
    if (name && name !== syncedMerchantName()) await pushMerchantName(existing, name);
    return existing;
  }

  const client = db();
  if (!client) return null;

  try {
    const { data: sess } = await client.auth.getSession();
    const userId = sess?.session?.user?.id;
    if (!userId) return null; // ยังไม่ล็อกอิน -> ปล่อยให้ค้างคิวไว้ก่อน

    // 🔴 ต้องหาแถวเดิมก่อน ห้าม upsert รวดเดียว
    //    upsert แบบ onConflict จะ UPDATE ทุกคอลัมน์ที่ส่งไป
    //    เครื่องที่สองที่ยังไม่ได้ตั้งชื่อร้านจึงเขียนทับชื่อจริงด้วยค่า default
    //    และล้าง segment/province เป็น null ทั้งที่เครื่องแรกกรอกไว้แล้ว
    //    (เจอจริงบน iPad 18 ก.ย. 2026 — ร้าน H2H กลายเป็น "ยังไม่ตั้งชื่อ")
    const found = await findMerchantRow(client, userId);
    if (found) return adoptMerchantRow(found, name);

    const { data, error } = await client
      .from('merchants')
      .insert({
        owner_user_id: userId,
        merchant_code: getMerchantCode(),
        name: name || DEFAULT_MERCHANT_NAME,
        segment: profile.segment || null,
        province: profile.province || null,
        source: profile.source || 'organic',
        stage: 'installed',
      })
      .select('id, name')
      .single();

    if (error || !data) {
      // ชนกับอีกเครื่องที่ insert พร้อมกัน -> แถวมีแล้ว ไปหยิบมาใช้
      const raced = await findMerchantRow(client, userId);
      return raced ? adoptMerchantRow(raced, name) : null;
    }

    try { localStorage.setItem(MERCHANT_ID_KEY, data.id); } catch (e) { /* ไม่เป็นไร */ }
    if (name) rememberMerchantName(name);
    return data.id;
  } catch (e) {
    return null;
  }
}

// ── บันทึกใบเสร็จ ────────────────────────────────────────────────
async function flushQueue(merchantId) {
  const queue = readQueue();
  if (queue.length === 0 || !db() || !merchantId) return;

  const rows = queue.map((r) => ({ ...r, merchant_id: merchantId }));
  try {
    // ignoreDuplicates: ส่งซ้ำได้ไม่พัง เพราะ share_token เป็น unique
    const { error } = await db()
      .from('pos_receipts')
      .upsert(rows, { onConflict: 'share_token', ignoreDuplicates: true });
    if (!error) writeQueue([]);
  } catch (e) {
    /* ยังออฟไลน์ เก็บไว้รอบหน้า */
  }
}

/**
 * บันทึกใบเสร็จ 1 ใบ — เรียกตอน commit การขายเท่านั้น
 * (พรีวิวที่ยังไม่ยืนยัน ห้ามนับ ไม่งั้น install_per_receipt จะต่ำเกินจริง)
 *
 * ห้าม await ในเส้นทางการขาย — ปล่อยให้วิ่งเบื้องหลัง
 */
export function recordReceipt({ token, docNo, total, itemCount, issuedAt, shopName }) {
  if (!LOOP_ENABLED || !token) return;

  const row = {
    share_token: token,
    doc_no: docNo || null,
    total_amount: Number(total) || 0,
    item_count: Number(itemCount) || 0,
    issued_at: issuedAt || new Date().toISOString(),
  };

  // เข้าคิวก่อนเสมอ แล้วค่อยพยายามส่ง — ถ้าแอปถูกปิดกลางคัน ข้อมูลไม่หาย
  writeQueue([...readQueue(), row]);

  (async () => {
    // เรียก ensureMerchant เสมอ ไม่ลัดด้วย localMerchantId() เหมือนเดิม
    // เพราะขาอัปเดตชื่อร้านอยู่ข้างใน — ลัดแล้วชื่อจะไม่มีวันถูกส่ง
    // (ข้างในคืนค่าทันทีถ้าชื่อไม่เปลี่ยน จึงไม่ได้เพิ่มการยิงเน็ต)
    const merchantId = await ensureMerchant({ name: shopName });
    if (!merchantId) return;
    await flushQueue(merchantId);
  })();
}

/** เรียกตอนเปิดหน้า POS เพื่อเคลียร์คิวที่ค้างจากวันก่อน */
export function syncPendingReceipts(profile = {}) {
  if (!LOOP_ENABLED) return;
  (async () => {
    const merchantId = await ensureMerchant(profile);
    if (merchantId) await flushQueue(merchantId);
  })();
}

/** จำนวนใบที่ยังส่งไม่ขึ้น — เอาไปโชว์ในหน้าตั้งค่าได้ */
export function pendingReceiptCount() {
  return readQueue().length;
}

// ── ขาเปิดแอปจากใบเสร็จ ──────────────────────────────────────────
//
// ปุ่ม "เปิดในแอป (ถ้าติดตั้งแล้ว)" บนหน้า /r/ ยิง moneyma://r/<token>
// ลิงก์นี้เปิดแอปได้ก็ต่อเมื่อแอปติดตั้งอยู่ก่อนแล้ว = คนเดิมกลับมา
// 🔴 จึงบันทึกเป็น app_opened_at ห้ามเขียนลง installed_at
//    ไม่งั้น install_per_receipt_pct จะพองด้วยคนที่ไม่ได้ติดตั้งจากใบเสร็จเลย
//    (ดู supabase/03_app_open.sql)
//
// 🔴 ส่งแค่โทเคนใบเสร็จ ไม่ส่ง user_id โดยตั้งใจ
//    ไม่ผูกตัวตนผู้ใช้ = ไม่มีข้อมูลส่วนตัวชนิดใหม่ ไม่ต้องแก้ App Privacy
//    ของ App Store หรือ Data safety ของ Play

// ป้ายบอกรุ่นโค้ด — ASCII ล้วน ค้นใน console ของ Xcode ด้วย [receipt-link]
// ไม่เห็นบรรทัด build=... ตอนเปิดแอป = แอปใน Simulator ยังเป็นบิลด์เก่า หยุดวิเคราะห์
// เปลี่ยนค่าทุกครั้งที่แก้ตรรกะขานี้ (กฎเดียวกับ RC_CLIENT_BUILD ใน rcClient.js)
export const APP_LINK_BUILD = 'applink-2026-10-05b-card';
const short = (t) => `${String(t).slice(0, 6)}...`;

const APP_OPEN_PENDING_KEY = 'moneyma_app_open_pending';
const APP_OPEN_DONE_KEY = 'moneyma_app_open_done';
const RECEIPT_TOKEN_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{16}$/;

function readList(key) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
}

function writeList(key, list, cap) {
  try {
    localStorage.setItem(key, JSON.stringify(list.slice(-cap)));
  } catch (e) {
    /* เต็มก็ปล่อย */
  }
}

/**
 * แกะโทเคนใบเสร็จจากลิงก์ที่เปิดแอป — คืน null ถ้าไม่ใช่ลิงก์ใบเสร็จ
 *
 * รับ: moneyma://r/<token> · moneyma:///r/<token> · https://…/r/?t=<token>
 *
 * 🔴 ต้องไม่จับ moneyma://auth… เด็ดขาด นั่นคือ OAuth กับรีเซ็ตรหัสผ่าน
 *    ถ้าจับผิด คนจะล็อกอินด้วย Google/Apple ไม่ได้ทั้งแอป
 */
export function parseReceiptDeepLink(url) {
  const s = String(url || '');
  const m =
    /^[a-z][a-z0-9+.-]*:\/\/\/?r\/([^/?#]+)/i.exec(s) ||   // moneyma://r/<token>
    // ต้องขึ้นต้นด้วย http(s) และ /r อยู่ที่ path ตรง ๆ — กันลิงก์ OAuth ที่บังเอิญ
    // มี /r/?t= ซ่อนอยู่ใน query (เช่น redirect=) ถูกจับผิดตัว
    /^https?:\/\/[^/?#]+\/r\/?\?(?:[^#]*&)?t=([^&#]+)/i.exec(s); // https://…/r/?t=<token>
  if (!m) return null;
  let token = '';
  try {
    token = decodeURIComponent(m[1]).trim().toUpperCase();
  } catch (e) {
    return null;
  }
  return RECEIPT_TOKEN_RE.test(token) ? token : null;
}

/**
 * ส่งโทเคนที่ค้างขึ้น Supabase — เรียกตอนเปิดแอปทุกครั้ง
 * ลบออกจากคิวเฉพาะตอนสำเร็จ ถ้าออฟไลน์หรือยังไม่ได้รัน 03_app_open.sql
 * จะลองใหม่รอบหน้าเอง ไม่หายเงียบ
 */
export async function flushPendingAppOpens() {
  const pending = readList(APP_OPEN_PENDING_KEY);
  // พิมพ์ทุกครั้งที่เปิดแอป แม้คิวว่าง — บรรทัดนี้คือหลักฐานว่ากำลังรันโค้ดรุ่นไหน
  console.log(`[receipt-link] build=${APP_LINK_BUILD} loop=${LOOP_ENABLED ? 'on' : 'off'} pending=${pending.length}`);
  if (!LOOP_ENABLED) return;
  const client = db();
  if (!client || pending.length === 0) return;

  const done = new Set(readList(APP_OPEN_DONE_KEY));
  const left = [];
  for (const token of pending) {
    if (done.has(token)) continue;
    try {
      const { error } = await client.rpc('record_receipt_app_open', { p_token: token });
      if (error) {
        left.push(token);
        console.log(`[receipt-link] rpc error ${short(token)} ${error.code || ''} ${error.message || ''}`);
      } else {
        done.add(token);
        console.log(`[receipt-link] rpc ok ${short(token)}`);
      }
    } catch (e) {
      left.push(token);
      console.log(`[receipt-link] offline, queued ${short(token)} ${e && e.message ? e.message : ''}`);
    }
  }
  writeList(APP_OPEN_PENDING_KEY, left, 50);
  writeList(APP_OPEN_DONE_KEY, [...done], 200);
}

/**
 * เรียกจากตัวดักลิงก์ของแอป — คืน true ถ้าเป็นลิงก์ใบเสร็จ
 * (คืน true แม้ loop ปิดอยู่ ตัวดักลิงก์จะได้ไม่ส่งต่อไปให้ OAuth
 *  ซึ่งจะ log error ทิ้งเปล่า ๆ)
 *
 * ห้าม await ในเส้นทางเปิดแอป — ยิงเบื้องหลังแล้วปล่อย
 */
export function recordAppOpenFromLink(url) {
  const token = parseReceiptDeepLink(url);
  if (!token) return false;

  const already = readList(APP_OPEN_DONE_KEY).includes(token);
  console.log(`[receipt-link] caught ${short(token)}${already ? ' (already recorded on this device)' : ''}`);

  // ให้หน้าจอโชว์การ์ดใบเสร็จ — เปิดซ้ำก็โชว์ซ้ำได้ (การ์ดจะบอกเองว่าบันทึกไว้แล้ว)
  queueReceiptToShow(token);
  if (LOOP_ENABLED && !already) {
    const pending = readList(APP_OPEN_PENDING_KEY);
    if (!pending.includes(token)) writeList(APP_OPEN_PENDING_KEY, [...pending, token], 50);
    flushPendingAppOpens();
  }
  return true;
}

// ── การ์ดใบเสร็จในแอปของลูกค้า ───────────────────────────────────
//
// หน้า /r/ สัญญากับลูกค้าว่า "เก็บใบเสร็จนี้ไว้ในมือถือ ฟรี"
// พอกด "เปิดในแอป" แล้วแอปต้องโชว์ใบเสร็จนั้นให้บันทึกเป็นรายจ่าย
// ไม่ใช่แค่เปิดหน้าแรกเฉย ๆ (เวอร์ชันแรกทำแบบนั้น ผู้ใช้เปิดมาแล้วไม่เห็นบิล)
//
// 🔴 ลิงก์มาถึงตอนแอปเพิ่งเปิด ซึ่งบางทีหน้าจอหลักยังไม่ขึ้น (ติดหน้าล็อกอินอยู่)
//    จึงเก็บโทเคนลง localStorage ด้วย ไม่ใช่ยิง event อย่างเดียว
//    หน้าจอหลักขึ้นมาเมื่อไหร่ค่อยหยิบไปโชว์ — event มีไว้สำหรับกรณีแอปเปิดค้างอยู่แล้ว

const RECEIPT_TO_SHOW_KEY = 'moneyma_receipt_to_show';
const IMPORTED_RECEIPTS_KEY = 'moneyma_imported_receipts';
export const RECEIPT_LINK_EVENT = 'moneyma:receipt-link';

// หมวดเดียวกับที่ AI สแกนใบเสร็จใช้กับสินค้าที่ไม่รู้ประเภท (BillScanService: other -> Shopping)
// ใบเสร็จจาก QR ไม่มีรายการสินค้า จึงจัดเป็นหมวดเดียวกัน รายงานจะได้ไม่แตกเป็นสองกลุ่ม
export const RECEIPT_IMPORT_CATEGORY = 'Shopping';

function queueReceiptToShow(token) {
  try { localStorage.setItem(RECEIPT_TO_SHOW_KEY, token); } catch (e) { /* ไม่เป็นไร */ }
  try {
    window.dispatchEvent(new CustomEvent(RECEIPT_LINK_EVENT, { detail: { token } }));
  } catch (e) { /* เบราว์เซอร์เก่ามาก */ }
}

/** โทเคนที่รอโชว์ (ไม่ลบออก) */
export function peekReceiptToShow() {
  try {
    const t = localStorage.getItem(RECEIPT_TO_SHOW_KEY);
    return t && RECEIPT_TOKEN_RE.test(t) ? t : null;
  } catch (e) {
    return null;
  }
}

/** เอาออกจากคิวโชว์ — เรียกตอนผู้ใช้ปิดการ์ดหรือบันทึกแล้ว */
export function clearReceiptToShow() {
  try { localStorage.removeItem(RECEIPT_TO_SHOW_KEY); } catch (e) { /* ไม่เป็นไร */ }
}

export function isReceiptImported(token) {
  return readList(IMPORTED_RECEIPTS_KEY).includes(token);
}

export function markReceiptImported(token) {
  const list = readList(IMPORTED_RECEIPTS_KEY);
  if (!list.includes(token)) writeList(IMPORTED_RECEIPTS_KEY, [...list, token], 500);
}

/**
 * อ่านใบเสร็จจากเซิร์ฟเวอร์ — อ่านอย่างเดียว ไม่นับเป็นการสแกน
 * (ใช้ get_public_receipt ไม่ใช่ record_receipt_scan ซึ่งบวก scan_count ทุกครั้ง)
 *
 * @returns {Promise<{ok:true, receipt:object} | {ok:false, reason:'not_found'|'offline'|'unavailable'}>}
 */
export async function fetchPublicReceipt(token) {
  const client = db();
  if (!client) return { ok: false, reason: 'unavailable' };
  try {
    const { data, error } = await client.rpc('get_public_receipt', { p_token: token });
    if (error) {
      console.log(`[receipt-link] fetch error ${short(token)} ${error.code || ''} ${error.message || ''}`);
      // PGRST202 = ยังไม่ได้รัน 04_public_receipt.sql บนเซิร์ฟเวอร์
      return { ok: false, reason: 'unavailable' };
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return { ok: false, reason: 'not_found' };
    return {
      ok: true,
      receipt: {
        shopName: row.shop_name || '',
        total: Number(row.total_amount) || 0,
        issuedAt: row.issued_at || null,
        itemCount: Number(row.item_count) || 0,
        docNo: row.doc_no || '',
      },
    };
  } catch (e) {
    return { ok: false, reason: 'offline' };
  }
}

/**
 * วันที่ตามเวลาเครื่อง (yyyy-mm-dd)
 * 🔴 ห้ามใช้ toISOString() — นั่นคือ UTC ใบเสร็จตอนตี 1 ที่ไทย
 *    จะกลายเป็นรายจ่ายของเมื่อวาน และไปตกผิดเดือนตอนสิ้นเดือน
 */
export function localYmd(value) {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return undefined;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** แปลงใบเสร็จเป็นรายการรายจ่าย — ใช้ทางเพิ่มรายการเดียวกับหน้า POS */
export function receiptToTransaction(receipt, describe) {
  return {
    type: 'expense',
    amount: Math.abs(Number(receipt.total)) || 0,
    category: RECEIPT_IMPORT_CATEGORY,
    description: describe(receipt.shopName),
    date: localYmd(receipt.issuedAt),
  };
}
